import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from "chart.js";
import config from "../../../config";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15,23,42,.72)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16
  },
  modal: {
    width: "100%",
    maxWidth: 1150,
    maxHeight: "92vh",
    background: "#fff",
    borderRadius: 18,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column"
  },
  header: {
    padding: "18px 24px",
    background: "linear-gradient(135deg,#1e3a5f,#2563eb)",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  body: {
    padding: 22,
    overflowY: "auto"
  },
  close: {
    border: 0,
    background: "rgba(255,255,255,.2)",
    color: "#fff",
    width: 34,
    height: 34,
    borderRadius: "50%",
    cursor: "pointer",
    fontSize: 22
  },
  grid4: {
    display: "grid",
    gridTemplateColumns: "repeat(4,1fr)",
    gap: 12,
    marginBottom: 18
  },
  card: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: 14
  },
  label: {
    fontSize: 12,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: ".4px"
  },
  value: {
    display: "block",
    marginTop: 6,
    fontSize: 24,
    fontWeight: 800,
    color: "#0f172a"
  },
  session: {
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    marginBottom: 14,
    overflow: "hidden"
  },
  sessionHeader: {
    padding: 16,
    background: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer"
  },
  charts: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
    padding: 16
  },
  chartBox: {
    height: 220,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12
  },
  badge: {
    padding: "5px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700
  },
  reportBox: {
    background: "#ffffff",
    border: "1px solid #dbeafe",
    borderRadius: 16,
    padding: 18,
    margin: "0 16px 18px"
  },
  reportTitle: {
    margin: "0 0 12px",
    color: "#1e293b",
    fontSize: 20
  },
  reportList: {
    paddingLeft: 22,
    lineHeight: 1.9,
    color: "#334155",
    marginBottom: 0
  },
  recommendationBox: {
    marginTop: 16,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: 14,
    padding: 16,
    color: "#334155",
    lineHeight: 1.7
  },
  footer: {
    padding: 16,
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "flex-end"
  },
  btn: {
    border: 0,
    borderRadius: 10,
    padding: "10px 22px",
    background: "#1e3a5f",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer"
  }
};

const safeNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const pct = (v) => `${Math.round(safeNum(v) * 100)}%`;
const score = (v) => `${Math.round(safeNum(v))}%`;
const wpm = (v) => `${Math.round(safeNum(v))} WPM`;

const avg = (arr) => {
  const nums = arr.map(Number).filter(Number.isFinite);
  if (!nums.length) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
};

const chartData = (values = [], label = "Data") => ({
  labels: values.map((_, i) => `Point ${i + 1}`),
  datasets: [
    {
      label,
      data: values,
      borderWidth: 2,
      pointRadius: 3,
      tension: 0.35,
      fill: true
    }
  ]
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false }
  },
  scales: {
    y: { beginAtZero: true }
  }
};

const extractValues = (data) => {
  if (!data) return [];
  if (Array.isArray(data.values)) return data.values;
  if (Array.isArray(data)) {
    return data.map((x) => {
      if (typeof x === "number") return x;
      if (typeof x?.value === "number") return x.value;
      if (typeof x?.avg === "number") return x.avg;
      if (typeof x?.kcal === "number") return x.kcal;
      return 0;
    });
  }
  return [];
};

const getLevel = (value, high = 75, medium = 55) => {
  const n = safeNum(value);
  if (n >= high) return "Strong";
  if (n >= medium) return "Moderate";
  return "Needs Improvement";
};

const getFinalRecommendation = (overallScore) => {
  const n = safeNum(overallScore);
  if (n >= 85) return "Highly Recommended";
  if (n >= 70) return "Recommended";
  if (n >= 55) return "Recommended With Training";
  return "Not Recommended Currently";
};

const buildMilitaryReportPoints = ({ metrics = {}, health = {}, overallScore = 0, progress = 0 }) => {
  const eye = safeNum(metrics.avgEyeContact) * 100;
  const speech = safeNum(metrics.avgSpeechRate);
  const blink = safeNum(metrics.avgBlinkRate);
  const tension = safeNum(metrics.avgFacialTension) * 100;
  const smile = safeNum(metrics.avgSmileAuthenticity) * 100;
  const headTilt = safeNum(metrics.avgHeadTiltOpenness) * 100;
  const pitch = safeNum(metrics.avgPitchVariability) * 100;
  const pauses = safeNum(metrics.avgPauses);
  const tremor = safeNum(metrics.avgVocalTremor) * 100;
  const warmth = safeNum(metrics.avgWarmthResonance) * 100;
  const interruptions = safeNum(metrics.totalInterruptions);
  const healthScore = safeNum(health?.overall_health_score || health?.score);
  const stress = safeNum(health?.stress_level);

  return [
    `Overall military readiness is ${getLevel(overallScore, 80, 60)} with a total session score of ${score(overallScore)}.`,
    `Eye contact score is ${Math.round(eye)}%, which shows ${eye >= 75 ? "strong focus, confidence, and visual discipline" : eye >= 55 ? "acceptable focus but needs more consistency" : "low confidence, weak visual focus, or possible stress avoidance"}.`,
    `Speech rate is ${Math.round(speech)} WPM, indicating ${speech >= 90 && speech <= 150 ? "balanced communication speed suitable for military interviews" : speech < 90 ? "slow response delivery that may show hesitation or low confidence" : "fast delivery that may indicate pressure, nervousness, or rushed thinking"}.`,
    `Blink rate is ${Math.round(blink)} per minute, showing ${blink <= 12 ? "calm visual behavior" : blink <= 22 ? "normal visual response" : "possible nervousness or pressure symptoms"}.`,
    `Facial tension is ${Math.round(tension)}%, meaning the candidate shows ${tension <= 40 ? "calm facial control" : tension <= 65 ? "moderate pressure signs" : "high visible stress during the session"}.`,
    `Smile authenticity is ${Math.round(smile)}%, reflecting ${smile >= 70 ? "positive emotional openness and confidence" : smile >= 50 ? "neutral emotional expression" : "limited emotional openness or guarded behavior"}.`,
    `Head posture openness is ${Math.round(headTilt)}%, showing ${headTilt >= 70 ? "open and confident body posture" : headTilt >= 50 ? "average posture control" : "closed or less confident posture"}.`,
    `Pitch variability is ${Math.round(pitch)}%, showing ${pitch >= 65 ? "strong vocal confidence and energy" : pitch >= 45 ? "moderate voice control" : "low vocal energy or confidence"}.`,
    `Pause pattern score is ${Math.round(pauses)}, meaning the candidate shows ${pauses <= 2 ? "smooth response flow" : pauses <= 5 ? "some hesitation while answering" : "frequent hesitation or difficulty forming responses"}.`,
    `Vocal tremor is ${Math.round(tremor)}%, suggesting ${tremor <= 30 ? "stable voice under pressure" : tremor <= 55 ? "some nervousness" : "high nervousness or stress response"}.`,
    `Warmth and resonance is ${Math.round(warmth)}%, indicating ${warmth >= 70 ? "strong interpersonal communication tone" : warmth >= 50 ? "acceptable communication tone" : "low warmth or weak communication presence"}.`,
    `Interruptions count is ${interruptions}, showing ${interruptions <= 1 ? "disciplined listening and response timing" : interruptions <= 3 ? "moderate response control" : "poor interruption control and low conversational discipline"}.`,
    `Question completion progress is ${progress || 0}%, which means the candidate ${progress >= 90 ? "completed the session strongly" : progress >= 60 ? "partially completed the session but still needs more data" : "did not complete enough answers for reliable evaluation"}.`,
    `Health fitness score is ${healthScore ? score(healthScore) : "N/A"}, and stress level is ${stress ? score(stress) : "N/A"}.`,
    `Final recommendation for this session is ${getFinalRecommendation(overallScore)}.`
  ];
};

const StatCard = ({ label, value }) => (
  <div style={S.card}>
    <div style={S.label}>{label}</div>
    <span style={S.value}>{value}</span>
  </div>
);

const MilitarySessionReportModal = ({
  show,
  soldierId,
  member,
  token,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [openWeek, setOpenWeek] = useState(null);

  useEffect(() => {
    if (!show || !soldierId) return;

    const fetchReport = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await fetch(
          `${config.baseURL}/api/military/soldiers/${soldierId}/report`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const data = await res.json();

        if (!data.success) {
          throw new Error(data.message || "Failed to load report");
        }

        setReport(data);
        setOpenWeek(data.sessions?.[0]?.week_number || null);
      } catch (err) {
        setError(err.message || "Failed to load report");
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [show, soldierId, token]);

  const averages = useMemo(() => {
    const sessions = report?.sessions || [];
    const withAnalytics = sessions.filter((s) => s.analytics);

    const metrics = withAnalytics.map((s) => s.analytics.metrics || {});
    const overallScores = withAnalytics.map((s) => s.overall_score || s.analytics.overallScore);

    return {
      completedSessions: sessions.filter((s) => s.status === "completed").length,
      totalSessions: 6,
      averageScore: avg(overallScores),
      eyeContact: avg(metrics.map((m) => m.avgEyeContact)),
      blinkRate: avg(metrics.map((m) => m.avgBlinkRate)),
      speechRate: avg(metrics.map((m) => m.avgSpeechRate)),
      interruptions: avg(metrics.map((m) => m.totalInterruptions)),
      pitch: avg(metrics.map((m) => m.avgPitchVariability)),
      pauses: avg(metrics.map((m) => m.avgPauses)),
      tremor: avg(metrics.map((m) => m.avgVocalTremor)),
      warmth: avg(metrics.map((m) => m.avgWarmthResonance))
    };
  }, [report]);

  if (!show) return null;

  const name =
    member?.candidate_name ||
    `${member?.first_name || ""} ${member?.last_name || ""}`.trim() ||
    "Soldier";

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <div>
            <h2 style={{ margin: 0 }}>Military Session Report</h2>
            <p style={{ margin: "4px 0 0", opacity: 0.85 }}>
              {name} • Session-wise analytics and average report
            </p>
          </div>

          <button style={S.close} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={S.body}>
          {loading && <p>Loading report...</p>}

          {error && (
            <div style={{ ...S.card, color: "#b91c1c" }}>
              {error}
            </div>
          )}

          {!loading && !error && report && (
            <>
              <div style={S.grid4}>
                <StatCard
                  label="Completed Sessions"
                  value={`${averages.completedSessions}/${averages.totalSessions}`}
                />
                <StatCard
                  label="Average Score"
                  value={score(averages.averageScore)}
                />
                <StatCard
                  label="Average Eye Contact"
                  value={pct(averages.eyeContact)}
                />
                <StatCard
                  label="Average Speech Rate"
                  value={wpm(averages.speechRate)}
                />
              </div>

              <div style={S.grid4}>
                <StatCard label="Avg Blink Rate" value={Math.round(averages.blinkRate)} />
                <StatCard label="Avg Interruptions" value={Math.round(averages.interruptions)} />
                <StatCard label="Avg Pitch" value={pct(averages.pitch)} />
                <StatCard label="Avg Warmth" value={pct(averages.warmth)} />
              </div>

              {(report.sessions || []).map((session) => {
                const analytics = session.analytics || {};
                const metrics = analytics.metrics || {};
                const chart = analytics.chartData || {};
                const health = analytics.health || {};
                const isOpen = openWeek === session.week_number;

                const statusColor =
                  session.status === "completed"
                    ? { background: "#dcfce7", color: "#166534" }
                    : session.status === "active"
                    ? { background: "#dbeafe", color: "#1d4ed8" }
                    : { background: "#fef3c7", color: "#92400e" };

                return (
                  <div key={session.session_id} style={S.session}>
                    <div
                      style={S.sessionHeader}
                      onClick={() =>
                        setOpenWeek(isOpen ? null : session.week_number)
                      }
                    >
                      <div>
                        <strong>Week {session.week_number} Interview</strong>
                        <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                          Questions: {session.answered_count}/{session.total_questions} •
                          Progress: {session.progress}%
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <span style={{ ...S.badge, ...statusColor }}>
                          {session.status}
                        </span>
                        <strong>{score(session.overall_score)}</strong>
                      </div>
                    </div>

                    {isOpen && (
                      <>
                        <div style={{ ...S.grid4, padding: 16, marginBottom: 0 }}>
                          <StatCard label="Overall Score" value={score(session.overall_score)} />
                          <StatCard label="Eye Contact" value={pct(metrics.avgEyeContact)} />
                          <StatCard label="Speech Rate" value={wpm(metrics.avgSpeechRate)} />
                          <StatCard label="Interruptions" value={metrics.totalInterruptions || 0} />
                        </div>

                        <div style={S.reportBox}>
                          <h3 style={S.reportTitle}>🪖 Detailed Military Fitness Report</h3>

                          <ul style={S.reportList}>
                            {buildMilitaryReportPoints({
                              metrics,
                              health,
                              overallScore: session.overall_score,
                              progress: session.progress
                            }).map((point, index) => (
                              <li key={index}>{point}</li>
                            ))}
                          </ul>

                          <div style={S.recommendationBox}>
                            <strong style={{ color: "#1e3a8a" }}>Final Recommendation:</strong>
                            <p style={{ margin: "8px 0 0" }}>
                              Based on the candidate’s overall score, eye contact,
                              voice stability, video behavior, facial confidence,
                              interruption control, response completion, and health
                              indicators, this session result is marked as{" "}
                              <strong>{getFinalRecommendation(session.overall_score)}</strong>.
                            </p>
                          </div>
                        </div>

                        <div style={{ ...S.grid4, padding: "0 16px" }}>
                          <StatCard label="Facial Tension" value={pct(metrics.avgFacialTension)} />
                          <StatCard label="Smile" value={pct(metrics.avgSmileAuthenticity)} />
                          <StatCard label="Pitch" value={pct(metrics.avgPitchVariability)} />
                          <StatCard label="Vocal Tremor" value={pct(metrics.avgVocalTremor)} />
                        </div>

                        <div style={S.charts}>
                          <div style={S.chartBox}>
                            <strong>Eye Contact</strong>
                            <Line
                              data={chartData(
                                extractValues(chart?.camera?.eyeContact),
                                "Eye Contact"
                              )}
                              options={chartOptions}
                            />
                          </div>

                          <div style={S.chartBox}>
                            <strong>Blink Rate</strong>
                            <Line
                              data={chartData(
                                extractValues(chart?.camera?.blinkRate),
                                "Blink Rate"
                              )}
                              options={chartOptions}
                            />
                          </div>

                          <div style={S.chartBox}>
                            <strong>Speech Rate</strong>
                            <Line
                              data={chartData(
                                extractValues(chart?.voice?.speechRate),
                                "Speech Rate"
                              )}
                              options={chartOptions}
                            />
                          </div>

                          <div style={S.chartBox}>
                            <strong>Pitch Variability</strong>
                            <Line
                              data={chartData(
                                extractValues(chart?.voice?.pitchVariability),
                                "Pitch Variability"
                              )}
                              options={chartOptions}
                            />
                          </div>
                        </div>

                        {health && Object.keys(health).length > 0 && (
                          <>
                            <h4 style={{ margin: "8px 16px 12px" }}>
                              Health Analytics
                            </h4>

                            <div style={{ ...S.grid4, padding: "0 16px" }}>
                              <StatCard
                                label="Health Score"
                                value={score(health.overall_health_score)}
                              />
                              <StatCard
                                label="Stress Level"
                                value={score(health.stress_level)}
                              />
                              <StatCard
                                label="Health Status"
                                value={health.health_status || "N/A"}
                              />
                              <StatCard
                                label="Stress Label"
                                value={health.stress_label || "N/A"}
                              />
                            </div>
                          </>
                        )}

                        {analytics.interpretation && (
                          <div style={S.charts}>
                            <div style={S.card}>
                              <strong>Strengths</strong>
                              <ul>
                                {(analytics.interpretation.strengths || []).map((x, i) => (
                                  <li key={i}>{x}</li>
                                ))}
                              </ul>
                            </div>

                            <div style={S.card}>
                              <strong>Areas to Improve</strong>
                              <ul>
                                {(analytics.interpretation.improvements || []).map((x, i) => (
                                  <li key={i}>{x}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}

                        {!analytics.metrics && (
                          <div style={{ ...S.card, margin: 16 }}>
                            No analytics data saved for this session yet.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={S.footer}>
          <button style={S.btn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MilitarySessionReportModal;